<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import DeepdiveLink from "../utils/DeepdiveLink.vue";
import { useAsyncValidator } from "@vueuse/integrations/useAsyncValidator";
import type { Rules } from "async-validator";
import type { UserConfig } from '@/lib/interfaces/calculator';
import getDeepdiveUrl from "@/lib/getDeepdiveUrl";
import { vOnClickOutside } from "@vueuse/components";
import type { OnClickOutsideHandler, OnClickOutsideOptions } from "@vueuse/core";

const showAutopilotPopup = ref<boolean>(false);

interface Props {
  availableCoinsAmount: number
}

const props = defineProps<Props>()
const userConfigAutopilot = defineModel<UserConfig['autopilot']>('autopilot', { required: true });

const isAuto = ref(userConfigAutopilot.value.coins === 'auto')

const form = reactive({
  coinCount: userConfigAutopilot.value.coins !== 'auto' ? userConfigAutopilot.value.coins : '0'
})

const rules: Rules = {
  coinCount: {
    type: "number",
    transform: (v) => Number(v),
    asyncValidator(rule, value) {

      return new Promise(res => {

        const max = rule?.max ?? 0

        if(value > max) {
          form.coinCount = max.toString()
        }

        res()

      })
    },
    min: 0,
    max: props.availableCoinsAmount,
  },
};

useAsyncValidator(form, rules);


watch([() => form.coinCount, isAuto], () => {

  userConfigAutopilot.value.coins = isAuto.value ? 'auto' : form.coinCount

})

const onClickOutsideHandler: [OnClickOutsideHandler, OnClickOutsideOptions] = [
  () => {
    showAutopilotPopup.value = false
  },
  { },
]

</script>

<template>
  <div class="flex justify-between items-center w-full mt-4.5">
    <span class="font-semibold text-brand-secondary"> Autopilot </span>
    <DeepdiveLink label="What’s this?" :href="getDeepdiveUrl('details/calculator/autopilot')" />
  </div>
  <div class="relative w-full mt-2.5">
    <button
      @click="showAutopilotPopup = !showAutopilotPopup"
      class="w-full px-4 py-2.5 justify-between flex items-center border-2 border-brand-stroke/10 rounded-full"
    >
      <span class="text-left truncate inline-block mr-2" >
        <span 
          v-if="userConfigAutopilot.enable"
          class="font-semibold text-sm text-brand-secondary"
        >
          <span class="text-brand-gradient">
            {{ $t('enabled') }},
          </span>
          {{ $t('set_to') }}
          <span class="text-brand-gradient">
            {{ userConfigAutopilot.coins }}
          </span>
          {{ $t('coin_count') }}.
        </span>
        <span
          v-else
          class="font-semibold text-sm text-brand-secondary"
        >
          {{ $t('disabled') }}.
        </span>        
      </span>
      <span class="i-ri:edit-line bg-brand-gradient text-lg inline-block" />
    </button>
    <div class="absolute top-0 w-full z-10">
      <Transition
        enter-active-class="duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
      >
        <div
          class="border-2 border-brand-stroke/10 rounded-3 p-5 w-full bg-brand-background z-100"
          v-show="showAutopilotPopup"
          v-on-click-outside="onClickOutsideHandler"
        >
          <div class="flex justify-between items-center">
            <span class="font-semibold">Enable autopilot</span>
            <div class="flex items-center gap-x-2.5">
              <span :class="['text-sm text-brand-primary']" >
                {{ userConfigAutopilot.enable ? $t('enabled') : $t('disabled') }}
              </span>
              <button
                @click="userConfigAutopilot.enable = !userConfigAutopilot.enable"
                class="border-2 rounded-full border-brand-primary p-1.5 w-12.5 flex items-center"
              >
                <div
                  :class="[
                    'rounded-full w-4.5 h-4.5 duration-300 ease-in-out',
                    userConfigAutopilot.enable ? 'translate-x-full bg-brand-primary' : 'bg-brand-primary/20',
                  ]"
                />
              </button>
            </div>
          </div>
          <div :class="!userConfigAutopilot.enable && 'opacity-10 pointer-events-none'" >
            <div 
              class="mt-6 flex justify-between items-center duration-300 ease-in-out"
            >
              <span class="font-semibold">Coin count</span>
              <DeepdiveLink label="What's this for?" :href="getDeepdiveUrl('details/calculator/autopilot')" />
            </div>
            <div 
              class="mt-2.5 inline-flex gap-x-2.5 relative"
            >
              <div
                @click="isAuto = false"
                class="w-24 rounded-3 border-brand-primary"
              >
                <input
                  v-model="form.coinCount"
                  type="number"
                  min="0"
                  :max="props.availableCoinsAmount"
                  id="calculator-autopilot-coin-count"
                  :class="[
                    'p-3 w-full outline-none bg-transparent rounded-3 text-base sm:text-sm border-2 font-semibold text-brand-secondary',
                    'placeholder:(text-base sm:text-sm font-bold text-brand-secondary)',
                    userConfigAutopilot.coins === 'auto'
                      ? 'border-brand-stroke/10 focus:(border-brand-primary)'
                      : 'border-brand-primary',
                  ]"
                />
              </div>
              <button
                @click="isAuto = true"
                :class="[
                  'w-16 relative border-2 rounded-3 grid place-content-center text-base sm:text-sm font-semibold',
                  userConfigAutopilot.coins === 'auto'
                    ? 'border-brand-primary'
                    : 'border-brand-stroke/10 ',
                ]"
              >
                {{ $t('auto') }}
              </button>
            </div>
          </div>
        </div>      
      </Transition>
    </div>
  </div>
</template>
